import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import {
  createNewProduct,
  checkProductExistence,
} from "../../../server/Product";
import PopUp from "../../containers/PopUp";
import { getDictByKind } from "../../../server/Category";
import ProductsWithFilters from "./ProductsWithFilters";
import "./Products.scss";
import showAlert from "../../../server/ShowAlert";
import { getAllCategory } from "../../../server/Category";
import { Select } from "../../containers/InputForm";
import { showAlertWithConfirmCancel } from "../../../server/ShowAlert";

const productSchema = yup
  .object({
    name: yup
      .string()
      .required("נדרש שם מוצר")
      .max(35, "שם המוצר חייב להיות עד 35 תווים"),
    gtin: yup
      .string()
      .matches(/^\d*$/, "ברקוד חייב להיות מספרי")
      .test(
        "gtin-length",
        "ברקוד חייב להיות 8, 12, 13 או 14 ספרות",
        (val) => !val || [8, 12, 13, 14].includes(val.length),
      )
      .required("נדרש ברקוד"),
    brand: yup
      .object()
      .shape({ id: yup.string().required("נדרש ספק") })
      .required("נדרש ספק"),
    subBrand: yup
      .object()
      .shape({ id: yup.string().required("נדרש מותג") })
      .required("נדרש מותג"),
    userCompany: yup.string().required("נדרש יצרן"),
    status: yup.string().required("נדרש סטטוס"),
    slug: yup.string().required("נדרשת קטגוריה"),
  })
  .required();

const emptyProduct = {
  gtin: "",
  name: "",
  description: "",
  brand: { id: "", name: "", tenantKind: "hamazon-brand" },
  subBrand: { id: "", name: "", tenantKind: "hamazon-sub-brand" },
  userCompany: "",
  status: "",
  media: [],
  kosherSupervision: [],
};

const CategorySelector = ({ categories, onSlugChange, onClearError }) => {
  const [levels, setLevels] = useState([
    { options: categories || [], selected: null },
  ]);

  const LEVEL_LABELS = ["מחלקה", "קטגוריה", "תת-קטגוריה", "מקבץ"];

  useEffect(() => {
    setLevels([{ options: categories || [], selected: null }]);
  }, [categories]);

  const handleSelect = (levelIndex, slug) => {
    const currentLevel = levels[levelIndex];
    const selectedOption = currentLevel.options.find(
      (item) => item.slug === slug,
    );

    const newLevels = levels
      .slice(0, levelIndex + 1)
      .map((level, index) =>
        index === levelIndex
          ? { ...level, selected: selectedOption || null }
          : level,
      );

    if (!selectedOption) {
      const previousSlug =
        levelIndex > 0 ? newLevels[levelIndex - 1]?.selected?.slug || "" : "";

      onSlugChange(previousSlug);
      setLevels(newLevels);
      return;
    }

    onSlugChange(selectedOption.slug);
    if (onClearError) onClearError();

    if (
      selectedOption.children &&
      Array.isArray(selectedOption.children) &&
      selectedOption.children.length > 0
    ) {
      newLevels.push({
        options: selectedOption.children,
        selected: null,
      });
    }

    setLevels(newLevels);
  };

  const handleRemoveLevel = (levelIndex) => {
    const newLevels = levels.slice(0, levelIndex);

    if (newLevels.length === 0) {
      setLevels([{ options: categories || [], selected: null }]);
      onSlugChange("");
      return;
    }

    newLevels[newLevels.length - 1].selected = null;

    const parentSlug =
      newLevels.length > 1
        ? newLevels[newLevels.length - 2]?.selected?.slug || ""
        : "";

    onSlugChange(parentSlug);
    setLevels(newLevels);
  };

  return (
    <div className="category-selector">
      {levels.map((level, idx) => (
        <div key={idx} className="category-level">
          <label className="label">
            {LEVEL_LABELS[idx] || `רמה ${idx + 1}`}
          </label>

          <div className="category-row">
            <select
              className={`select-input category-select ${
                level.selected ? "has-value" : ""
              }`}
              value={level.selected?.slug || ""}
              onChange={(e) => handleSelect(idx, e.target.value)}
            >
              <option value="">בחר {LEVEL_LABELS[idx] || "קטגוריה"}</option>

              {level.options.map((option) => (
                <option key={option.slug} value={option.slug}>
                  {option.name}
                </option>
              ))}
            </select>

            {level.selected && (
              <button
                type="button"
                className="remove-level-btn"
                onClick={() => handleRemoveLevel(idx + 1)}
                title="הסר"
              >
                ✕
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};
const FormField = ({
  label,
  name,
  type = "text",
  value,
  onChange,
  error,
  options = [],
  required = false,
  placeholder = "",
}) => {
  const isSelect = type === "select";
  const isCheckbox = type === "checkbox";
  const isTextarea = type === "textarea";

  if (isCheckbox) {
    return (
      <label className="checkbox-label">
        <input
          type="checkbox"
          name={name}
          checked={value}
          onChange={onChange}
          className="checkbox-input"
        />
        <span>{label}</span>
      </label>
    );
  }

  if (isSelect) {
    return (
      <div className="form-group">
        <label className="label">
          {label} {required && <span className="required">*</span>}
        </label>
        <Select
          name={name}
          options={options}
          value={value}
          onChange={onChange}
          errors={error ? { [name]: { message: error } } : {}}
        />
      </div>
    );
  }

  return (
    <div className="form-group">
      <label className="label">
        {label} {required && <span className="required">*</span>}
      </label>

      {isTextarea ? (
        <textarea
          name={name}
          value={value}
          onChange={onChange}
          className={`input textarea-input ${error ? "input-error" : ""}`}
          rows={2}
          placeholder={placeholder}
        />
      ) : (
        <input
          type={type}
          name={name}
          value={value}
          onChange={onChange}
          className={`input ${error ? "input-error" : ""}`}
          placeholder={placeholder}
        />
      )}

      {error && <div className="error-message">{error}</div>}
    </div>
  );
};

const Products = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [newProduct, setNewProduct] = useState({ ...emptyProduct });
  const [brands, setBrands] = useState([]);
  const [subBrands, setSubBrands] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState("");

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const statusOptions = [
    { id: "active", name: "פעיל" },
    { id: "invactive", name: "לא פעיל" },
  ];

  const { categories, stationId } = useSelector((state) => ({
    categories: state.category.categories,
    stationId: state.user.stationId,
  }));

  useEffect(() => {
    dispatch(getAllCategory(stationId, null, 4, true, true));
  }, [stationId, dispatch]);

  useEffect(() => {
    getDictByKind("brand").then(setBrands).catch(console.error);
    getDictByKind("sub-brand").then(setSubBrands).catch(console.error);
  }, []);

  const openModal = () => {
    setIsModalOpen(true);
    setFormErrors({});
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setNewProduct({ ...emptyProduct });
    setFormErrors({});
    setSelectedSlug("");
  };

  const validateForm = () => {
    try {
      productSchema.validateSync(
        { ...newProduct, slug: selectedSlug },
        { abortEarly: false },
      );
      return {};
    } catch (err) {
      const errors = {};
      err.inner.forEach((e) => {
        errors[e.path] = e.message;
      });
      return errors;
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "brand") {
      const sel = brands.find((b) => b.id === value);
      setNewProduct((p) => ({
        ...p,
        brand: sel
          ? {
              id: sel.id,
              name: sel.name,
              tenantKind: "hamazon-brand",
            }
          : { id: "", name: "", tenantKind: "hamazon-brand" },
      }));
    } else if (name === "subBrand") {
      const sel = subBrands.find((b) => b.id === value);
      setNewProduct((p) => ({
        ...p,
        subBrand: sel
          ? {
              id: sel.id,
              name: sel.name,
              tenantKind: "hamazon-sub-brand",
            }
          : { id: "", name: "", tenantKind: "hamazon-sub-brand" },
      }));
    } else {
      setNewProduct((p) => ({
        ...p,
        [name]: type === "checkbox" ? checked : value,
      }));
    }

    if (formErrors[name]) {
      setFormErrors((p) => ({ ...p, [name]: undefined }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    if (newProduct.gtin) {
      try {
        const existsResponse = await checkProductExistence(
          newProduct.gtin,
          stationId,
        );
        if (existsResponse?.data) {
          showAlertWithConfirmCancel({
            msgTitle: "המוצר כבר קיים",
            msgText: `מוצר עם ברקוד ${newProduct.gtin} כבר קיים במערכת. האם ברצונך לעבור אליו?`,
            confirmText: "עבור למוצר",
            cancelText: "צור וריאנט חדש",
            onCancel: () => {
              createProd();
              closeModal();
            },
            onConfirm: () => {
              navigate(
                `/edit-product/${existsResponse.data.productId || newProduct.gtin + "-000"}/information`,
                {
                  state: { from: "/admin/products" },
                },
              );
              closeModal();
            },
          });
          return;
        }
      } catch (err) {
        console.error("Error checking product existence:", err);
      }
    }
    createProd();
  };

  const createProd = async () => {
    const payload = {
      ...newProduct,
      branch: stationId,
      slug: selectedSlug,
      media: [],
      kosherSupervision: [],
      vatExempt: false,
      weightDetails: {
        weighedProduct: false,
      },
      expiredManagement: true,
      viewBrand: true,
    };

    try {
      const response = await createNewProduct({
        gtin: newProduct.gtin,
        data: payload,
      });

      if (response.status === 201) {
        showAlert({ msgTitle: "המוצר נוצר בהצלחה", msgType: "success" });
        closeModal();
        navigate(
          `/edit-product/${newProduct.gtin}-${response.data?.product?.variant || "000" || ""}/information`,
        );
      } else {
        showAlert({ msgTitle: "שגיאה ביצירת מוצר", msgType: "error" });
      }
    } catch (error) {
      console.error(error);
      showAlert({ msgTitle: "שגיאה ביצירת מוצר", msgType: "error" });
    }
  };

  return (
    <div className="products-header">
      <ProductsWithFilters openModal={openModal} />

      {isModalOpen && (
        <PopUp width={620} close={closeModal}>
          <div className="modal-content">
            <h3 className="modal-title">יצירת מוצר חדש</h3>

            <form onSubmit={handleSubmit} className="product-form">
              <div className="grid-2">
                <FormField
                  label="שם מוצר"
                  name="name"
                  value={newProduct.name}
                  onChange={handleInputChange}
                  error={formErrors.name}
                  required
                />
                <FormField
                  label="ברקוד"
                  name="gtin"
                  type="number"
                  value={newProduct.gtin}
                  onChange={handleInputChange}
                  error={formErrors.gtin}
                  required
                />
              </div>

              <div className="grid-2">
                <FormField
                  label="ספק"
                  name="brand"
                  type="select"
                  value={newProduct.brand.id}
                  onChange={handleInputChange}
                  error={formErrors["brand.id"]}
                  options={brands}
                  required
                />
                <FormField
                  label="מותג"
                  name="subBrand"
                  type="select"
                  value={newProduct.subBrand.id}
                  onChange={handleInputChange}
                  error={formErrors["subBrand.id"]}
                  options={subBrands}
                  required
                />
              </div>

              <div className="grid-2">
                <FormField
                  label="יצרן"
                  name="userCompany"
                  value={newProduct.userCompany}
                  onChange={handleInputChange}
                  error={formErrors.userCompany}
                  required
                />
                <FormField
                  label="סטטוס"
                  name="status"
                  type="select"
                  value={newProduct.status}
                  onChange={handleInputChange}
                  error={formErrors.status}
                  options={statusOptions}
                  required
                />
              </div>
              <FormField
                label="תיאור"
                name="description"
                value={newProduct.description}
                onChange={handleInputChange}
              />
              <div className="section-divider">
                <span>קטגוריה</span>
              </div>

              <CategorySelector
                categories={categories || []}
                onSlugChange={setSelectedSlug}
                onClearError={() =>
                  setFormErrors((p) => ({ ...p, slug: undefined }))
                }
              />
              {formErrors.slug && (
                <div className="error-message">{formErrors.slug}</div>
              )}

              <div className="buttons-container">
                <button
                  type="button"
                  className="cancel-button"
                  onClick={closeModal}
                >
                  ביטול
                </button>

                <button type="submit" className="save-button">
                  שמור מוצר
                </button>
              </div>
            </form>
          </div>
        </PopUp>
      )}
    </div>
  );
};

export default Products;
